import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Loader2, ArrowRight, FolderPlus, FileSignature, Archive, Info } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { STATUS_COLORS, LOSS_REASON_OPTIONS } from './detail/constants';

const WHAT_HAPPENS = {
  Active: 'Reopens the tender for pricing. Any submitted date is cleared.',
  Submitted: 'Marks the quote as sent to the client and records the submitted date.',
  Won: 'Creates a Live project from this tender with its cost breakdown as the contract value.',
  Lost: 'Closes the tender as lost. You can record why, and send the feedback survey from the tender page.',
  Archived: 'Hides the tender from the working lists and moves its SharePoint folder to the Archive.',
  Void: 'Marks the tender as void — it drops out of the pipeline and the win-rate figures.',
};

function Row({ icon: Icon, children, testId }) {
  return <div className="flex items-start gap-2.5 text-sm text-gray-300" data-testid={testId}><Icon className="w-4 h-4 mt-0.5 shrink-0 text-[#F5A623]" /><div className="flex-1 min-w-0">{children}</div></div>;
}

// Confirm a status change straight from the dashboard — with the bits each status needs.
export function QuickStatusDialog({ tender, nextStatus, onClose, onDone }) {
  const { api, user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [submittedDate, setSubmittedDate] = useState(tender.submitted_date || format(new Date(), 'yyyy-MM-dd'));
  const [createFolders, setCreateFolders] = useState(true);
  const [requestContract, setRequestContract] = useState(false);
  const [lossReason, setLossReason] = useState('');
  const [lossNotes, setLossNotes] = useState('');

  const confirm = async () => {
    setBusy(true);
    try {
      const body = { status: nextStatus };
      if (nextStatus === 'Submitted') body.submitted_date = submittedDate || null;
      if (nextStatus === 'Won') body.create_folders = createFolders;
      if (nextStatus === 'Lost') { body.loss_reason = lossReason || null; body.loss_reason_notes = lossNotes.trim() || null; }
      const r = await api.put(`/tenders/${tender.id}`, body);
      const by = user?.name || 'user';
      const notes = [`Status changed from ${tender.status} to ${nextStatus} from the dashboard by ${by}`];
      if (nextStatus === 'Won') notes.push(createFolders ? 'Project folders created in SharePoint' : `Project folder creation skipped by ${by} — create them from the project when ready`);
      if (nextStatus === 'Lost' && lossReason) notes.push(`Loss reason recorded: ${lossReason}${lossNotes.trim() ? ` — ${lossNotes.trim()}` : ''}`);
      if (nextStatus === 'Won' && requestContract) {
        await api.put(`/tenders/${tender.id}`, { contract_status: 'Generation Requested' });
        notes.push(`Contract and H&S generation requested by ${by}`);
      }
      for (const note of notes) await api.post(`/tenders/${tender.id}/notes`, { note }).catch(() => {});
      const projectId = r.data?.converted_project_id;
      if (nextStatus === 'Won' && projectId) {
        toast.success(`T${tender.tender_number} won — Live project created`, { action: { label: 'Open project', onClick: () => window.location.assign(`/projects/${projectId}`) }, duration: 8000 });
      } else {
        toast.success(`T${tender.tender_number} is now ${nextStatus}`);
      }
      onDone?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change the status');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-[#1E2530] border-white/10 text-white sm:max-w-md" onClick={(e) => e.stopPropagation()} data-testid="quick-status-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 flex-wrap">
            <span style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-[#F5A623] text-xl">T{tender.tender_number}</span>
            <Badge className={`${STATUS_COLORS[tender.status] || ''} text-xs`}>{tender.status}</Badge>
            <ArrowRight className="w-4 h-4 text-gray-500" />
            <Badge className={`${STATUS_COLORS[nextStatus] || ''} text-xs`} data-testid="quick-status-next">{nextStatus}</Badge>
          </DialogTitle>
          <DialogDescription className="text-gray-400">{tender.client_name} · {tender.address}{tender.postcode ? `, ${tender.postcode}` : ''}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Row icon={Info} testId="quick-status-explain">{WHAT_HAPPENS[nextStatus]}</Row>

          {nextStatus === 'Submitted' && (
            <div>
              <label className="text-xs text-gray-400 block mb-1">Submitted on</label>
              <Input type="date" value={submittedDate} onChange={(e) => setSubmittedDate(e.target.value)} className="bg-[#2A3240] border-white/20 text-white" data-testid="quick-status-submitted-date" />
            </div>
          )}

          {nextStatus === 'Won' && (
            <div className="space-y-3 rounded-lg border border-white/10 bg-white/5 p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={createFolders} onCheckedChange={(v) => setCreateFolders(!!v)} className="mt-0.5 border-white/40 data-[state=checked]:bg-[#F5A623] data-[state=checked]:border-[#F5A623]" data-testid="quick-status-create-folders" />
                <span className="text-sm"><span className="text-white font-medium inline-flex items-center gap-1.5"><FolderPlus className="w-4 h-4 text-[#F5A623]" /> Create the project folders in SharePoint</span><span className="block text-xs text-gray-400 mt-0.5">Sets up the Live Jobs folder structure and copies the tender files across. Untick to do this later from the project.</span></span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={requestContract} onCheckedChange={(v) => setRequestContract(!!v)} className="mt-0.5 border-white/40 data-[state=checked]:bg-[#F5A623] data-[state=checked]:border-[#F5A623]" data-testid="quick-status-request-contract" />
                <span className="text-sm"><span className="text-white font-medium inline-flex items-center gap-1.5"><FileSignature className="w-4 h-4 text-[#F5A623]" /> Request the contract &amp; H&amp;S pack</span><span className="block text-xs text-gray-400 mt-0.5">Flags the tender for contract generation.</span></span>
              </label>
            </div>
          )}

          {nextStatus === 'Lost' && (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Why did we lose it? (optional)</label>
                <Select value={lossReason} onValueChange={setLossReason}>
                  <SelectTrigger className="bg-[#2A3240] border-white/20 text-white" data-testid="quick-status-loss-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">{LOSS_REASON_OPTIONS.map((o) => <SelectItem key={o} value={o} className="text-white focus:bg-[#F5A623]/20">{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Textarea value={lossNotes} onChange={(e) => setLossNotes(e.target.value)} rows={2} placeholder="Anything worth remembering…" className="bg-[#2A3240] border-white/20 text-white resize-none" data-testid="quick-status-loss-notes" />
            </div>
          )}

          {nextStatus === 'Archived' && <Row icon={Archive} testId="quick-status-archive-note">The tender folder moves to <span className="text-white">01. Tenders / Archive</span>. Nothing is deleted.</Row>}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose} disabled={busy} className="text-gray-300 hover:text-white hover:bg-white/10" data-testid="quick-status-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={busy} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#f7b84a] font-semibold" data-testid="quick-status-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{nextStatus === 'Won' ? 'Mark as won' : `Change to ${nextStatus}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
